import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Card } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Pill } from '../components/ui/Pill'
import { Icon, I } from '../components/ui/icons'
import { gridColumns, mobileButtonStyle, TEXT_WRAP_STYLE } from '../components/ui'
import { useIsMobileViewport } from '../hooks/useIsMobileViewport'
import { getInventoryConnectionSummary, listInventoryConnections } from '../inventory/inventoryApi'
import type { InventoryConnection, InventoryConnectionSummary } from '../inventory/types'
import { listSourceAuthorities, updateSourceAuthority } from '../source/sourceApi'
import type { SourceAuthority, SourceAuthorityUpdateRequest, SourceDomain } from '../source/types'

interface InventoryPageProps {
  accessToken: string
  onOpenTlaaq?: () => void
  onOpenZoho?: () => void
}

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div>
      <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--fg-subtle)' }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--brand-900)', letterSpacing: '-0.01em', marginTop: 4 }}>{value}</div>
      {sub && <div style={{ fontSize: 11.5, color: 'var(--fg-subtle)', marginTop: 2 }}>{sub}</div>}
    </div>
  )
}

export function InventoryPage({ accessToken, onOpenTlaaq, onOpenZoho }: InventoryPageProps) {
  const { t } = useTranslation()
  const isMobile = useIsMobileViewport()
  const [connections, setConnections] = useState<InventoryConnection[]>([])
  const [summary, setSummary] = useState<InventoryConnectionSummary | null>(null)
  const [authorities, setAuthorities] = useState<SourceAuthority[]>([])
  const [loading, setLoading] = useState(true)
  const [sourceSaving, setSourceSaving] = useState<SourceDomain | null>(null)
  const [sourceError, setSourceError] = useState(false)

  useEffect(() => {
    let cancelled = false
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLoading(true)
    Promise.allSettled([listInventoryConnections(accessToken), listSourceAuthorities(accessToken)])
      .then(([connectionsResult, authoritiesResult]) => {
        if (cancelled) return
        setConnections(connectionsResult.status === 'fulfilled' ? connectionsResult.value : [])
        if (authoritiesResult.status === 'fulfilled') {
          setAuthorities(authoritiesResult.value)
          setSourceError(false)
        } else {
          setAuthorities([])
          setSourceError(true)
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [accessToken])

  const zoho = useMemo(() => connections.find(connection => connection.provider === 'ZOHO') ?? null, [connections])
  const zohoConnected = zoho?.status === 'CONNECTED'
  const catalogAuthority = authorities.find(authority => authority.domain === 'PRODUCT_CATALOG')
  const stockAuthority = authorities.find(authority => authority.domain === 'STOCK')

  const sourceIsZoho = (authority: SourceAuthority | undefined) => Boolean(
    authority?.ownerType === 'INVENTORY_CONNECTION'
    && (authority.inventoryConnectionId === zoho?.id || authority.provider === 'ZOHO'),
  )
  const sourceIsTlaaq = (authority: SourceAuthority | undefined) => !authority || authority.ownerType === 'TLAAQ'
  const sourceName = (authority: SourceAuthority | undefined) => {
    if (sourceIsTlaaq(authority)) return t('pages.inventory.tlaaq')
    if (sourceIsZoho(authority)) return t('pages.inventory.zoho')
    if (authority?.ownerType === 'CHANNEL_CONNECTION') {
      return authority.provider
        ? t(`channels.providers.${authority.provider}`)
        : t('channels.source.owners.channelFallback')
    }
    return t('channels.source.owners.inventoryFallback', { provider: authority?.provider ?? '' })
  }

  async function handleSourceChange(domain: SourceDomain, owner: 'TLAAQ' | 'ZOHO') {
    let payload: SourceAuthorityUpdateRequest
    if (owner === 'ZOHO') {
      if (!zohoConnected || !zoho) return
      payload = { ownerType: 'INVENTORY_CONNECTION', inventoryConnectionId: zoho.id, replaceExisting: true }
    } else {
      payload = { ownerType: 'TLAAQ', replaceExisting: true }
    }
    setSourceSaving(domain)
    setSourceError(false)
    try {
      const next = await updateSourceAuthority(accessToken, domain, payload)
      setAuthorities(current => {
        const byDomain = new Map(current.map(authority => [authority.domain, authority]))
        byDomain.set(next.domain, next)
        return Array.from(byDomain.values())
      })
    } catch {
      setSourceError(true)
    } finally {
      setSourceSaving(null)
    }
  }

  useEffect(() => {
    if (!zoho) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSummary(null)
      return
    }
    let cancelled = false
    getInventoryConnectionSummary(accessToken, zoho.id)
      .then(next => {
        if (!cancelled) setSummary(next)
      })
      .catch(() => {
        if (!cancelled) setSummary(null)
      })
    return () => {
      cancelled = true
    }
  }, [accessToken, zoho])

  const latestRun = summary?.latestRun ?? null
  const latestRunAt = latestRun?.finishedAt ?? latestRun?.updatedAt ?? null
  const latestRunLabel = latestRunAt
    ? new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(latestRunAt))
    : null
  const sourceRows = [
    {
      domain: 'PRODUCT_CATALOG' as const,
      authority: catalogAuthority,
      label: t('pages.inventory.source.catalog'),
      description: t('pages.inventory.source.catalogDescription'),
    },
    {
      domain: 'STOCK' as const,
      authority: stockAuthority,
      label: t('pages.inventory.source.stock'),
      description: t('pages.inventory.source.stockDescription'),
    },
  ]

  return (
    <div>
      <div style={{ display: isMobile ? 'grid' : 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 20 }}>
        <p style={{ fontSize: 13.5, color: 'var(--fg-muted)', margin: 0, maxWidth: 580 }}>
          {t('pages.inventory.description')}
        </p>
      </div>

      {/* Source-of-truth banner */}
      <Card padding={16} style={{ background: 'var(--brand-100)', borderColor: 'var(--brand-300)', marginBottom: 18 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
          <div style={{ width: 36, height: 36, borderRadius: 9, background: 'white', color: 'var(--brand-700)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Icon size={18}>{I.shield}</Icon>
          </div>
          <div style={{ flex: 1, ...TEXT_WRAP_STYLE }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--brand-900)' }}>
              {t('pages.inventory.sourceLabel')}
            </div>
            <div style={{ fontSize: 12, color: 'var(--brand-700)', marginTop: 2 }}>
              {t('pages.inventory.sourceDescription')}
            </div>
            {sourceError && (
              <div style={{ color: 'var(--danger)', fontSize: 12, marginTop: 8 }}>
                {t('pages.inventory.source.error')}
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: gridColumns(isMobile, 'repeat(2, minmax(0, 1fr))'), gap: 10, marginTop: 14 }}>
              {sourceRows.map(row => {
                const isZoho = sourceIsZoho(row.authority)
                const isTlaaq = sourceIsTlaaq(row.authority)
                const saving = sourceSaving === row.domain
                return (
                  <div key={row.domain} style={{ background: 'white', border: '1px solid var(--brand-200)', borderRadius: 'var(--r-md)', padding: 12, minWidth: 0 }}>
                    <div style={{ display: isMobile ? 'grid' : 'flex', justifyContent: 'space-between', gap: 10, alignItems: isMobile ? 'stretch' : 'center' }}>
                      <div style={TEXT_WRAP_STYLE}>
                        <div style={{ fontSize: 12.5, fontWeight: 800, color: 'var(--brand-900)' }}>{row.label}</div>
                        <div style={{ fontSize: 11.5, color: 'var(--fg-muted)', marginTop: 2 }}>{row.description}</div>
                      </div>
                      <Pill tone={isTlaaq ? 'neutral' : 'brand'} size="sm" dot>{sourceName(row.authority)}</Pill>
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                      <Button kind={isZoho ? 'secondary' : 'primary'} size="xs" disabled={saving || !zohoConnected || isZoho} onClick={() => void handleSourceChange(row.domain, 'ZOHO')} style={mobileButtonStyle(isMobile)}>
                        {saving ? t('pages.inventory.source.saving') : t('pages.inventory.source.useZoho')}
                      </Button>
                      <Button kind={isTlaaq ? 'secondary' : 'ghost'} size="xs" disabled={saving || isTlaaq} onClick={() => void handleSourceChange(row.domain, 'TLAAQ')} style={mobileButtonStyle(isMobile)}>
                        {saving ? t('pages.inventory.source.saving') : t('pages.inventory.source.useTlaaq')}
                      </Button>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </Card>

      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--fg-subtle)', marginBottom: 10 }}>
        {t('pages.inventory.connectedSources')}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: gridColumns(isMobile, 'repeat(2, minmax(0, 1fr))'), gap: 14 }}>
        {/* Tlaaq card */}
        <Card padding={20} style={{ cursor: 'pointer', borderColor: stockAuthority?.ownerType === 'TLAAQ' ? 'var(--brand-300)' : 'var(--border-subtle)' }} onClick={onOpenTlaaq}>
          <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
            <div style={{ width: 42, height: 42, borderRadius: 11, background: 'var(--grad-brand)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Icon size={20}>{I.layers}</Icon>
            </div>
            <div style={{ flex: 1, ...TEXT_WRAP_STYLE }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--brand-900)' }}>{t('pages.inventory.tlaaqInventory')}</div>
                {stockAuthority?.ownerType === 'TLAAQ' && <Pill tone="brand" dot size="sm">{t('pages.inventory.currentSource')}</Pill>}
              </div>
              <div style={{ fontSize: 12.5, color: 'var(--fg-muted)', marginTop: 5, lineHeight: 1.5 }}>
                {t('pages.inventory.tlaaqInventoryHint')}
              </div>
              <div style={{ marginTop: 14, color: 'var(--brand-700)', fontWeight: 700, fontSize: 12.5 }}>
                {t('pages.inventory.openTlaaq')}
              </div>
            </div>
            <Icon size={16} color="var(--fg-subtle)">{I.arrowR}</Icon>
          </div>
        </Card>

        {/* Zoho card */}
        <Card padding={20} style={{ cursor: 'pointer' }} onClick={onOpenZoho}>
          <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
            <div style={{ width: 42, height: 42, borderRadius: 11, background: 'linear-gradient(135deg, #C8202F, #E94B47)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 18, fontFamily: 'Nunito, sans-serif', flexShrink: 0 }}>Z</div>
            <div style={{ flex: 1, ...TEXT_WRAP_STYLE }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--brand-900)' }}>{t('pages.inventory.zoho')}</div>
                <Pill tone={zohoConnected ? 'success' : zoho ? 'warning' : 'neutral'} dot size="sm">
                  {loading ? t('pages.inventory.loading') : zohoConnected ? t('pages.inventory.connected') : zoho ? t('pages.inventory.pending') : t('pages.inventory.notConnected')}
                </Pill>
              </div>
              <div style={{ fontSize: 12, color: 'var(--fg-subtle)', marginTop: 3 }}>
                {zohoConnected && zoho?.externalAccountId ? zoho.externalAccountId : t('pages.inventory.connectZohoHint')}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(auto-fit, minmax(140px, 1fr))' : 'repeat(3, minmax(0, 1fr))', gap: isMobile ? 12 : 22, marginTop: 14 }}>
                <Stat label={t('pages.inventory.stats.products')} value={summary ? String(summary.productCount) : t('pages.inventory.stats.notAvailable')} sub={summary ? t('pages.inventory.stats.productsImported') : t('pages.inventory.stats.productsPending')} />
                <Stat label={t('pages.inventory.stats.syncRules')} value={t('pages.inventory.stats.notAvailable')} sub={t('pages.inventory.stats.rulesDeferred')} />
                <Stat label={t('pages.inventory.stats.lastRun')} value={latestRunLabel ?? t('pages.inventory.stats.notAvailable')} sub={latestRun ? t(`pages.inventory.runStatus.${latestRun.status}`) : t('pages.inventory.stats.lastRunPending')} />
              </div>
            </div>
            <Icon size={16} color="var(--fg-subtle)">{I.arrowR}</Icon>
          </div>
        </Card>

        {/* Request connector card */}
        <Card padding={20} style={{ borderStyle: 'dashed', background: 'transparent' }}>
          <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
            <div style={{ width: 42, height: 42, borderRadius: 11, background: 'var(--ink-100)', color: 'var(--fg-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Icon size={20}>{I.plus}</Icon>
            </div>
            <div style={{ flex: 1, ...TEXT_WRAP_STYLE }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--brand-900)' }}>{t('pages.inventory.requestTitle')}</div>
              <div style={{ fontSize: 12.5, color: 'var(--fg-muted)', marginTop: 4, lineHeight: 1.5 }}>
                {t('pages.inventory.requestBody')}
              </div>
              <div style={{ display: isMobile ? 'grid' : 'flex', gap: 8, marginTop: 12 }}>
                <Button kind="secondary" size="sm" style={mobileButtonStyle(isMobile)}>{t('pages.inventory.request')}</Button>
                <Button kind="ghost" size="sm" style={mobileButtonStyle(isMobile)}>{t('pages.inventory.viewApiDocs')}</Button>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  )
}
